function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toInteger(value, fallback = 0) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBoolFromYesNo(value) {
  return String(value || "").toLowerCase() === "yes";
}

function toIsoDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeCurrency(value) {
  const allowed = ["GMD", "USD", "GHC", "GBP"];
  const normalized = String(value || "").trim().toUpperCase();
  return allowed.includes(normalized) ? normalized : "GMD";
}

function normalizeSalaryFrequency(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "weekly") {
    return "Weekly";
  }
  if (raw === "quarterly") {
    return "Quarterly";
  }
  return "Monthly";
}

export function buildMotorPremiumPayload(values) {
  const startDate = values.startDate;
  const endDate = values.endDate;
  const extraTppd = toBoolFromYesNo(values.extraTppd);

  return {
    fullName: values.fullName,
    phone: values.phone,
    email: values.email,
    product: values.policyType,
    currency: normalizeCurrency(values.currency),
    currencyRate: 1,
    vehicles: [
      {
        risk: values.vehicleUsage,
        vehicleValue: toNumber(values.vehicleValue),
        passengers: toInteger(values.seatingCapacity, 1),
        cubicCapacity: toInteger(values.cubicCapacity, 0),
        yearOfManufacture: toInteger(values.yearOfManufacture, 0),
        registrationYear: toInteger(values.registrationYear, toInteger(values.yearOfManufacture, 0)),
        extraTppd,
        extraTppdAmount: extraTppd ? toNumber(values.extraTppdAmount) : 0,
        buyBackExcess: toBoolFromYesNo(values.buyBackExcess),
        startDate: toIsoDate(startDate),
        endDate: toIsoDate(endDate),
      },
    ],
  };
}

export function buildTravelPremiumPayload(values) {
  const startDate = new Date(values.departureDate);
  const endDate = new Date(values.returnDate);
  const diffMs = endDate.getTime() - startDate.getTime();
  const days = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 1;

  const countryCode =
    values.countryCode || String(values.countryName || "").slice(0, 3).toUpperCase();

  return {
    fullName: values.fullName,
    phone: values.phone,
    email: values.email,
    product: values.product || "Travel Insurance",
    currency: normalizeCurrency(values.currency),
    currencyRate: 1,
    travel: {
      coverage: values.coverage || "Single Trip",
      days,
      countryCode,
      countryName: values.countryName,
      age: toInteger(values.age, 30),
    },
  };
}

export function buildPaPremiumPayload(values) {
  const sumInsured = toNumber(values.sumInsured);
  const monthlyIncome = toNumber(values.monthlyIncome, sumInsured);
  const multiplier = toNumber(values.multiplier, 1);
  const permanentDisabilityBenefit = toNumber(
    values.permanentDisabilityBenefit,
    sumInsured
  );
  const temporalDisabilityBenefit = toNumber(
    values.temporalDisabilityBenefit,
    sumInsured
  );
  const medicalExpenseBenefitRate = toNumber(values.medicalExpenseBenefitRate, 1);
  const permanentDisabilityBenefitRate = toNumber(
    values.permanentDisabilityBenefitRate,
    1
  );
  const temporalDisabilityBenefitRate = toNumber(
    values.temporalDisabilityBenefitRate,
    1
  );
  const capitalBenefit = toNumber(values.capitalBenefit, sumInsured);
  const numberOfWeeks = toInteger(values.numberOfWeeks, 52);
  const weeklyBenefit = toNumber(values.weeklyBenefit, monthlyIncome / 4 || 0);

  return {
    fullName: values.fullName,
    phone: values.phone,
    email: values.email,
    product: values.product || "Personal Accident",
    currency: normalizeCurrency(values.currency),
    currencyRate: 1,
    paRisks: {
      startDate: toIsoDate(values.startDate),
      endDate: toIsoDate(values.endDate),
      medicalExpenseBenefit: sumInsured,
      medicalExpenseBenefitRate,
      permanentDisabilityBenefit,
      permanentDisabilityBenefitRate,
      temporalDisabilityBenefit,
      temporalDisabilityBenefitRate,
      salaryFrequency: normalizeSalaryFrequency(values.salaryFrequency),
      monthlyIncome,
      multiplier,
      capitalBenefit,
      numberOfWeeks,
      weeklyBenefit,
      totalSumInsured: sumInsured,
    },
  };
}

export function buildHomePremiumPayload(values) {
  return {
    fullName: values.fullName,
    phone: values.phone,
    email: values.email,
    product: "Homeowners",
    currency: normalizeCurrency(values.currency),
    currencyRate: 1,
    homeRisks: [
      {
        startDate: toIsoDate(values.effectiveDate),
        endDate: toIsoDate(values.expiryDate),
        sectionCode: values.coverType,
        sumInsured: toNumber(values.totalSumInsured),
      },
    ],
  };
}
